import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useCreateNCR, useUpdateNCR, NCR_CATEGORIES, NCRDetailed } from '@/hooks/useNCR';
import { Loader2, AlertTriangle } from 'lucide-react';

const ncrFormSchema = z.object({
  title: z.string().min(1, 'El título es requerido').max(255),
  description: z.string().min(1, 'La descripción es requerida'),
  ncr_type: z.enum(['internal', 'supplier', 'customer']),
  severity: z.enum(['critical', 'major', 'minor']),
  category: z.string().optional(),
  due_date: z.string().optional(),
  assigned_to: z.string().optional(),
  work_order_id: z.string().optional(),
  immediate_action: z.string().optional(),
});

type NCRFormData = z.infer<typeof ncrFormSchema>;

interface NCRFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ncr?: NCRDetailed | null;
  workOrderId?: string;
  inspectionId?: string;
}

export function NCRFormDialog({
  open,
  onOpenChange,
  ncr,
  workOrderId,
  inspectionId,
}: NCRFormDialogProps) {
  const createNCR = useCreateNCR();
  const updateNCR = useUpdateNCR();
  const isEditing = !!ncr;

  const form = useForm<NCRFormData>({
    resolver: zodResolver(ncrFormSchema),
    defaultValues: {
      title: '',
      description: '',
      ncr_type: 'internal',
      severity: 'minor',
      category: '',
      due_date: '',
      assigned_to: 'none',
      work_order_id: workOrderId || 'none',
      immediate_action: '',
    },
  });

  // Load users for assignment
  const { data: users } = useQuery({
    queryKey: ['users-for-assignment'],
    queryFn: async () => {
      const { data, error } = await api
        .from('profiles')
        .select('id, full_name')
        .order('full_name');
      if (error) throw error;
      return data;
    },
  });

  // Load work orders
  const { data: workOrders } = useQuery({
    queryKey: ['work-orders-active'],
    queryFn: async () => {
      const { data, error } = await api
        .from('work_orders')
        .select('id, order_number, product_name')
        .in('status', ['pending', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
  });

  // Reset form when dialog opens/closes or ncr changes
  useEffect(() => {
    if (open) {
      if (ncr) {
        form.reset({
          title: ncr.title,
          description: ncr.description,
          ncr_type: ncr.ncr_type,
          severity: ncr.severity,
          category: ncr.category || '',
          due_date: ncr.due_date || '',
          assigned_to: ncr.assigned_to || 'none',
          work_order_id: ncr.work_order_id || 'none',
          immediate_action: ncr.immediate_action || '',
        });
      } else {
        form.reset({
          title: '',
          description: '',
          ncr_type: 'internal',
          severity: 'minor',
          category: '',
          due_date: '',
          assigned_to: 'none',
          work_order_id: workOrderId || 'none',
          immediate_action: '',
        });
      }
    }
  }, [open, ncr, workOrderId, form]);

  const onSubmit = async (data: NCRFormData) => {
    const payload = {
      title: data.title,
      description: data.description,
      ncr_type: data.ncr_type,
      severity: data.severity,
      category: data.category || null,
      due_date: data.due_date || null,
      assigned_to: data.assigned_to && data.assigned_to !== 'none' ? data.assigned_to : null,
      work_order_id: data.work_order_id && data.work_order_id !== 'none' ? data.work_order_id : null,
      inspection_id: inspectionId || null,
      immediate_action: data.immediate_action || null,
    };

    if (isEditing && ncr) {
      await updateNCR.mutateAsync({ id: ncr.id, data: payload });
    } else {
      await createNCR.mutateAsync(payload);
    }

    onOpenChange(false);
  };

  const isSubmitting = createNCR.isPending || updateNCR.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            {isEditing ? 'Editar No Conformidad' : 'Nueva No Conformidad (NCR)'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? `Editando NCR ${ncr?.ncr_number}`
              : 'Registrar una nueva no conformidad en el sistema'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Descripción breve del problema" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="ncr_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="internal">Interna</SelectItem>
                        <SelectItem value="supplier">Proveedor</SelectItem>
                        <SelectItem value="customer">Cliente</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Severidad</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="critical">Crítica</SelectItem>
                        <SelectItem value="major">Mayor</SelectItem>
                        <SelectItem value="minor">Menor</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoría</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {NCR_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción del Problema *</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describa detalladamente la no conformidad encontrada..."
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="work_order_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden de Trabajo</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Vincular a OT" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Sin vincular</SelectItem>
                        {workOrders?.map((wo) => (
                          <SelectItem key={wo.id} value={wo.id}>
                            {wo.order_number} - {wo.product_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="assigned_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Asignar a</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Responsable" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {users?.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.full_name || 'Sin nombre'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha Límite</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="immediate_action"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Acción Inmediata (Contención)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Acciones tomadas para contener el problema..."
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEditing ? 'Guardar Cambios' : 'Crear NCR'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default NCRFormDialog;
